import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const SubscriptionGraph = ({ storageData, histogramData, counterData }) => {
  // Calculate Total Counts from histogramData
  const totalCounts = histogramData
    ? histogramData.reduce(
        (acc, item) => {
          acc.completedJobs += item.completedJobs || 0;
          acc.pendingJobs += item.pendingJobs || 0;
          acc.completedTasks += item.completedTasks || 0;
          acc.pendingTasks += item.pendingTasks || 0;
          acc.totalClients += item.totalClients || 0;
          return acc;
        },
        {
          completedJobs: 0,
          pendingJobs: 0,
          completedTasks: 0,
          pendingTasks: 0,
          totalClients: 0,
        }
      )
    : {};

  return (
    <div className="flex flex-col lg:flex-row gap-4 w-full">
      {/* Left Side - Subscription Storage Progress Bar & Counters */}
      <div className="w-full lg:w-1/2 bg-white shadow rounded-lg p-4">
        <h3 className="text-center text-lg font-semibold mb-2">
          Subscription Storage Usage
        </h3>
        {storageData ? (
          <div className="w-full">
            <div className="w-full bg-gray-200 rounded-full h-6">
              <div
                className="bg-[#135EA0] h-6 rounded-full text-center text-white font-semibold"
                style={{ width: `${storageData.percentage}%` }}
              >
                {storageData.percentage}%
              </div>
            </div>
            <p className="text-center mt-2 text-gray-700">
              {storageData.used} GB used of {storageData.total} GB
            </p>
          </div>
        ) : (
          <p className="text-center text-gray-500">No Storage Data Available</p>
        )}

        {/* Counter Cards - Visitors, Campaigns, Leaves, Documents Uploaded */}
        <div className="grid grid-cols-2 gap-4 mt-4">
          {/* Small Counter Card Component */}
          {counterData &&
            counterData.map(({ label, count, color }, index) => (
              <div
                key={index}
                className="bg-gray-100 shadow-sm p-3 rounded-lg text-center"
              >
                <p className="text-sm font-medium text-gray-600">{label}</p>
                <p className={`text-xl font-bold ${color}`}>{count}</p>
              </div>
            ))}
        </div>
      </div>

      {/* Right Side - Histogram (Bar Chart) + List of Counters */}
      <div className="w-full lg:w-1/2 bg-white shadow rounded-lg p-4">
        <h3 className="text-center text-lg font-semibold mb-2">
          Jobs, Tasks & Clients (Month-wise)
        </h3>
        {histogramData && histogramData.length > 0 ? (
          <>
            {/* Bar Chart */}
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={histogramData}>
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="completedJobs"
                  fill="#4caf50"
                  name="Completed Jobs"
                />
                <Bar dataKey="pendingJobs" fill="#ff9800" name="Pending Jobs" />
                <Bar
                  dataKey="completedTasks"
                  fill="#2196F3"
                  name="Completed Tasks"
                />
                <Bar
                  dataKey="pendingTasks"
                  fill="#f44336"
                  name="Pending Tasks"
                />
                <Bar
                  dataKey="totalClients"
                  fill="#8e44ad"
                  name="Total Clients"
                  barSize={15}
                />
              </BarChart>
            </ResponsiveContainer>

            {/* List of Counters (Two Per Row, No Icons) */}
            <div className="grid grid-cols-2 gap-4 mt-4 text-lg font-semibold">
              <p className="text-gray-700">
                Completed Jobs:{" "}
                <span className="text-[#4caf50]">
                  {totalCounts.completedJobs}
                </span>
              </p>
              <p className="text-gray-700">
                Pending Jobs:{" "}
                <span className="text-[#ff9800]">
                  {totalCounts.pendingJobs}
                </span>
              </p>
              <p className="text-gray-700">
                Completed Tasks:{" "}
                <span className="text-[#2196F3]">
                  {totalCounts.completedTasks}
                </span>
              </p>
              <p className="text-gray-700">
                Pending Tasks:{" "}
                <span className="text-[#f44336]">
                  {totalCounts.pendingTasks}
                </span>
              </p>
              <p className="text-gray-700 col-span-2 text-center">
                Total Clients:{" "}
                <span className="text-[#8e44ad]">
                  {totalCounts.totalClients}
                </span>
              </p>
            </div>
          </>
        ) : (
          <p className="text-center text-gray-500">
            No Job/Task/Client Data Available
          </p>
        )}
      </div>
    </div>
  );
};

export default SubscriptionGraph;
